import * as v from 'valibot';

import { isNumericIdString, reportThrottled } from '#shared';

import type { AlertFrame, AlertSnapshot } from '../model/types';

const REPORT_THROTTLE_MS = 60_000;

// bigint PK carried as a decimal string — shares the one numeric-id predicate
// with the domain-event schema and the seq cursor, so the check cannot drift.
const numericString = v.pipe(
  v.string(),
  v.check(isNumericIdString, 'must be a numeric id string'),
);

// Rejected rather than tolerated: `raisedAt` becomes the card's `raisedAtMs`,
// and a NaN there renders the age as `NaNh` instead of failing visibly.
const parseableTimestamp = v.pipe(
  v.string(),
  v.check(
    value => !Number.isNaN(Date.parse(value)),
    'must be a parseable timestamp',
  ),
);

export const alertFrameSchema = v.object({
  kind: v.literal('alert'),
  // Three members, per the BE contract (`updateMessage.ts:139`). Two would
  // reject every `updated` frame, and a rejected frame is dropped whole, so the
  // alert would silently stop refreshing (377:E028).
  transition: v.picklist(['raised', 'updated', 'cleared']),
  id: numericString,
  alertKey: v.pipe(v.string(), v.nonEmpty()),
  code: v.pipe(v.string(), v.nonEmpty()),
  group: v.string(),
  subjectType: v.pipe(v.string(), v.nonEmpty()),
  subjectId: v.pipe(v.string(), v.nonEmpty()),
  context: v.record(v.string(), v.unknown()),
  raisedAt: parseableTimestamp,
  lastObservedAt: parseableTimestamp,
  clearedAt: v.optional(v.string()),
  clearedReason: v.optional(v.picklist(['resolved', 'stale'])),
  ts: v.number(),
});

/** Parse a single wire item into a valid frame, or `null`. */
export const validateAlertFrame = (value: unknown): AlertFrame | null => {
  const parsed = v.safeParse(alertFrameSchema, value);

  return parsed.success ? (parsed.output as AlertFrame) : null;
};

/**
 * Parse the snapshot envelope; drops malformed rows, and answers an empty
 * snapshot for a bad envelope rather than throwing.
 *
 * A gap between the raw and kept counts is a contract-drift signal (the BE wire
 * shape diverging from this schema) — surface it, throttled, with a sample of
 * the first dropped row, instead of losing alerts silently (DATA-001).
 */
export const parseAlertSnapshot = (raw: unknown): AlertSnapshot => {
  if (typeof raw !== 'object' || raw === null) {
    return { alerts: [], asOf: 0 };
  }

  const { alerts, asOf } = raw as Record<string, unknown>;
  const parsedAsOf =
    typeof asOf === 'number' && Number.isFinite(asOf) ? asOf : 0;

  if (!Array.isArray(alerts)) {
    return { alerts: [], asOf: parsedAsOf };
  }

  const parsed = alerts
    .map(validateAlertFrame)
    .filter((frame): frame is AlertFrame => frame !== null);

  if (parsed.length < alerts.length) {
    reportThrottled(
      'alert-snapshot:dropped-rows',
      'alert snapshot: dropped rows failing schema validation',
      {
        throttleMs: REPORT_THROTTLE_MS,
        level: 'warning',
        extra: {
          droppedCount: alerts.length - parsed.length,
          rawCount: alerts.length,
          sample: alerts.find(item => validateAlertFrame(item) === null),
        },
      },
    );
  }

  return { alerts: parsed, asOf: parsedAsOf };
};
